/**
 * ReportsStatsCard - Afișează statistici despre rapoarte
 */

import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  Description as DescriptionIcon,
  Edit as EditIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material'
import { formatNumber, formatSeconds } from '../../hooks/useAnalytics'

interface ReportsStatsCardProps {
  totalReports: number
  draftCount: number
  finalizedCount: number
  avgLengthChars: number
  avgWordCount: number
  avgWritingTimeSec: number
  wordsPerMinute: number
  avgTimeToFinalizeSec: number
  postFinalizeEditsCount: number
  loading?: boolean
}

export const ReportsStatsCard: React.FC<ReportsStatsCardProps> = ({
  totalReports,
  draftCount,
  finalizedCount,
  avgLengthChars,
  avgWordCount,
  avgWritingTimeSec,
  wordsPerMinute,
  avgTimeToFinalizeSec,
  postFinalizeEditsCount,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card elevation={1} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', overflow: 'auto' }}>
        {/* Header - Compact */}
        <Box display="flex" alignItems="center" mb={1}>
          <DescriptionIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Rapoarte</Typography>
        </Box>

        {/* Stats principale - Compact */}
        <Grid container spacing={1} mb={1}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'primary.main' }}>{totalReports}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Total</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'success.main' }}>{finalizedCount}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Finalizate</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'warning.main' }}>{draftCount}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Draft</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        {/* Viteza scriere - Compact */}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Eficiență:</Typography>
        <Box mb={1}>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><SpeedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />Cuv/min:</Typography>
            <Chip label={`${wordsPerMinute.toFixed(0)}`} color={wordsPerMinute >= 80 ? 'success' : wordsPerMinute >= 60 ? 'warning' : 'default'} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
          </Box>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Lung. medie:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{formatNumber(Math.round(avgWordCount))} cuv</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Caractere:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{formatNumber(Math.round(avgLengthChars))}</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Timpi - Compact */}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Timpi:</Typography>
        <Box>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Scriere:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{formatSeconds(avgWritingTimeSec)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>La finalizare:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{formatSeconds(avgTimeToFinalizeSec)}</Typography>
          </Box>
          {postFinalizeEditsCount > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography sx={{ fontSize: '0.72rem', color: 'warning.main' }}><EditIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />Post-edit:</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: 'warning.main' }}>{postFinalizeEditsCount}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

